/**
 * Task hierarchy utilities for arranging tasks into depth-first parent-child order.
 *
 * @see PlanTasksTimelineView - primary consumer for timeline (Gantt) rendering
 */ // Models
/**
 * Arranges tasks into depth-first hierarchical order where each parent appears immediately
 * before its children. Orphans (tasks whose parent is not in the input list) are treated as
 * roots, so their own subtrees still expand; tasks trapped in a cycle are appended at depth 0.
 *
 * @param tasks The flat list of tasks to arrange hierarchically.
 * @param getChildren Resolver returning a parent's direct children — e.g. a store index
 * (`taskStore.getChildTasks`) in the grid, or an in-memory index over the input list. Resolved
 * children are scoped to `tasks`; roots always come from `tasks`.
 * @returns Flattened tasks and a depth map.
 */ export function arrangeTasksInHierarchicalOrder(tasks, getChildren) {
    const flattenedTasks = [];
    const depthMap = new Map();
    // Task ids in scope, used to keep resolved children within the input set.
    const inputIds = new Set(tasks.map((t)=>t.id));
    // Roots: tasks with no parent, plus orphans (parent not in the list) so their subtrees still expand.
    const roots = tasks.filter((task)=>task.parentTaskId == null || !inputIds.has(task.parentTaskId));
    // Direct children of a parent id, scoped to the input set.
    const resolveChildren = (parentId)=>getChildren(parentId).filter((task)=>inputIds.has(task.id));
    // Depth-first walk from roots; the depthMap guard prevents duplicate placement and cycles.
    const walk = (siblings, depth)=>{
        for (const task of siblings){
            if (depthMap.has(task.id)) {
                continue;
            }
            depthMap.set(task.id, depth);
            flattenedTasks.push(task);
            walk(resolveChildren(task.id), depth + 1);
        }
    };
    walk(roots, 0);
    // Safety net: append any task still unplaced (trapped in a cycle) at depth 0 so every task appears.
    if (flattenedTasks.length < tasks.length) {
        const seen = new Set(flattenedTasks.map((t)=>t.id));
        for (const task of tasks){
            if (!seen.has(task.id)) {
                depthMap.set(task.id, 0);
                flattenedTasks.push(task);
            }
        }
    }
    return {
        flattenedTasks,
        depthMap
    };
}
/**
 * Nearest task above `taskId` (in the flattened `orderedTasks`) that shares its parent — used by
 * "Make subtask" (indent). Scans backward rather than checking `index - 1`, since the row directly
 * above is often a deeper descendant of an earlier sibling, not the preceding sibling.
 *
 * @param taskId The task whose preceding sibling to find.
 * @param orderedTasks Tasks in display (depth-first) order.
 * @param isParentInPlan Reports whether a parent id exists in the plan; back it with the unfiltered plan
 * set so a subtask whose parent is only filtered out of `orderedTasks` isn't mistaken for an orphan.
 * @returns The preceding sibling's id, or `null` if the task is first among its siblings (or absent).
 */ export function getPrecedingSiblingId(taskId, orderedTasks, isParentInPlan) {
    const index = orderedTasks.findIndex((t)=>t.id === taskId);
    if (index <= 0) {
        return null;
    }
    // Match on the effective parent (null for an orphan whose parent is genuinely gone) so orphans find their root-level siblings.
    const effectiveParentId = (task)=>{
        const parentId = task.parentTaskId ?? null;
        return parentId != null && isParentInPlan(parentId) ? parentId : null;
    };
    const parentTaskId = effectiveParentId(orderedTasks[index]);
    for(let i = index - 1; i >= 0; i--){
        if (effectiveParentId(orderedTasks[i]) === parentTaskId) {
            return orderedTasks[i].id;
        }
    }
    return null;
}
/**
 * Hierarchy chain from a task's root down to the task itself
 *
 * @example
 * // Tree Root → A → B, called for B:
 * //   flattenedTasks: [Root, A, B]
 * //   depthMap:       { Root: 0, A: 1, B: 2 }
 * @param taskId Task whose ancestor chain to collect.
 * @param getTask Resolver returning a task by id.
 * @returns The task and its ancestors in root→leaf order with their depths (empty if the task is unresolvable).
 */ export function getAncestorChain(taskId, getTask) {
    const chain = [];
    // Guards against a self-referential parent chain looping forever.
    const visited = new Set();
    let currentId = taskId;
    // Walk up from the task itself until a root, a missing task, or an id we've already seen (cycle).
    while(currentId != null && !visited.has(currentId)){
        const current = getTask(currentId);
        if (current == null) {
            break;
        }
        visited.add(current.id);
        chain.push(current);
        currentId = current.parentTaskId;
    }
    // Walked leaf→root; reverse to present root→leaf and number depths from the topmost ancestor.
    chain.reverse();
    const depthMap = new Map();
    chain.forEach((task, depth)=>depthMap.set(task.id, depth));
    return {
        flattenedTasks: chain,
        depthMap
    };
}
/**
 * Collect the ids of every ancestor reachable by walking upward from each task.
 * @param tasks Tasks whose ancestor ids to collect.
 * @param getTask Resolver returning a task by id.
 * @returns Set of ancestor ids (excludes the input tasks' own ids).
 */ export function collectAncestorIds(tasks, getTask) {
    const inputIds = new Set(tasks.map((task)=>task.id));
    const ancestorIds = new Set();
    for (const task of tasks){
        let parentId = task.parentTaskId;
        // Walk up until a root, a missing ancestor, an input task, or an already-collected id
        while(parentId != null && !inputIds.has(parentId) && !ancestorIds.has(parentId)){
            const parent = getTask(parentId);
            if (parent == null) {
                break;
            }
            ancestorIds.add(parent.id);
            parentId = parent.parentTaskId;
        }
    }
    return ancestorIds;
}
